import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, AlertTriangle, ArrowRight, CheckCircle, Users, TrendingUp } from 'lucide-react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { Button } from '@/components/ui/Button';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const META_DESCRIPTION =
    "Funding a season of competitive sport adds up fast — equipment, travel, coaching, and entry fees. Calculate your season funding gap in 60 seconds — then close it with a Fund Page on Sport Fund.";

const YEAR_FEDERAL_LIMITS: Record<string, number> = {
    Freshman: 5500,
    Sophomore: 6500,
    Junior: 7500,
    Senior: 7500,
};

const PLUS_ANNUAL_CAP = 20000;
const MONTHLY_BACKER_AMOUNT = 50;

function dollars(n: number): string {
    return '$' + Math.round(n).toLocaleString('en-US');
}

type CalcResult = {
    coa: number;
    grants: number;
    family: number;
    federalMax: number;
    netNeedBeforePlus: number;
    newPlusCoverage: number;
    annualGap: number;
    monthlyGap: number;
    backersNeeded: number;
    yearLabel: string;
};

export default function ParentPlusCalculator() {
    usePageMeta('Season Funding Gap Calculator', META_DESCRIPTION);
    const navigate = useNavigate();
    const resultsRef = useRef<HTMLDivElement>(null);

    const [year, setYear] = useState<string>('');
    const [coa, setCoa] = useState<string>('');
    const [grants, setGrants] = useState<string>('');
    const [family, setFamily] = useState<string>('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string>('');

    const { ref: gtmRef, isVisible: gtmVisible } = useScrollAnimation();
    const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

    const result: CalcResult | null = useMemo(() => {
        if (!year || !coa) return null;
        const federalMax = YEAR_FEDERAL_LIMITS[year];
        const coaNum = parseFloat(coa) || 0;
        const grantsNum = parseFloat(grants) || 0;
        const familyNum = parseFloat(family) || 0;
        if (!federalMax || coaNum <= 0) return null;

        const netNeedBeforePlus = Math.max(0, coaNum - grantsNum - familyNum - federalMax);
        const newPlusCoverage = Math.min(netNeedBeforePlus, PLUS_ANNUAL_CAP);
        const annualGap = netNeedBeforePlus - newPlusCoverage;
        const monthlyGap = annualGap / 12;
        const backersNeeded = Math.ceil(monthlyGap / MONTHLY_BACKER_AMOUNT);

        return {
            coa: coaNum,
            grants: grantsNum,
            family: familyNum,
            federalMax,
            netNeedBeforePlus,
            newPlusCoverage,
            annualGap,
            monthlyGap,
            backersNeeded,
            yearLabel: year,
        };
    }, [year, coa, grants, family]);

    useEffect(() => {
        if (submitted && result && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [submitted, result]);

    const handleCalculate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!year) {
            setError('Pick your competition level.');
            return;
        }
        if (!coa || parseFloat(coa) <= 0) {
            setError('Enter your annual cost of competing.');
            return;
        }
        setError('');
        setSubmitted(true);
    };

    const goToSignup = () => {
        const params = new URLSearchParams(window.location.search);
        params.set('source', 'parent-plus-calculator');
        if (result) {
            params.set('annual_gap', String(Math.round(result.annualGap)));
            params.set('monthly_gap', String(Math.round(result.monthlyGap)));
        }
        navigate(`/signup-creator?${params.toString()}`);
    };

    const verdict = result ? getVerdict(result.annualGap, result.backersNeeded) : null;

    return (
        <div className="min-h-screen bg-background">
            <HomeHeader />

            {/* Hero */}
            <section className="relative pt-32 pb-16 px-6 bg-gradient-to-b from-secondary/20 to-background">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-white border border-border text-sm font-medium text-primary">
                        <AlertTriangle className="w-4 h-4" />
                        Plan ahead for the 2026 competition season
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6 leading-tight">
                        Competing isn't cheap.<br />
                        Find your season gap in 60 seconds.
                    </h1>
                    <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl mx-auto">
                        A full season can run <strong className="text-foreground">thousands a year</strong> once you add up{' '}
                        <strong className="text-foreground">equipment, travel, coaching, and entry fees</strong>. If grants and family support
                        don't cover it, the difference is now yours to close. This calculator shows exactly how big the gap is — and how to fill it.
                    </p>
                    <a href="#calc">
                        <Button
                            size="lg"
                            className="text-lg px-8 py-6 rounded-full font-semibold group"
                        >
                            Calculate my gap
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </a>
                    <p className="text-sm text-muted-foreground mt-4">Free. No signup to use. 60 seconds.</p>
                    {/* hero CTA */}
                </div>
            </section>

            {/* Stat tiles */}
            <section className="py-12 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <StatCard num="$5,000+/yr" label="Typical out-of-pocket cost for a competitive youth athlete" />
                        <StatCard num="$2,000+" label="Average annual spend on travel and entry fees alone" />
                        <StatCard num="5×" label="Elite-level families spend up to 5× the national average on sport" />
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-4">
                        Source: industry surveys of youth and amateur sports spending, 2026.
                    </p>
                </div>
            </section>

            {/* Calculator */}
            <section id="calc" className="py-16 px-6 bg-gradient-to-br from-primary/5 to-secondary/10">
                <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 mb-3 text-primary">
                            <Calculator className="w-5 h-5" />
                            <span className="text-sm font-semibold uppercase tracking-wider">Your numbers</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Four inputs. One real answer.</h2>
                        <p className="text-muted-foreground">Nothing leaves this page until you decide to act on the result.</p>
                    </div>

                    <form onSubmit={handleCalculate} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-border space-y-6">
                        <Field label="Competition level">
                            <select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                className="w-full px-4 py-3 text-base border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                required
                            >
                                <option value="" disabled>Pick your level…</option>
                                <option value="Freshman">Club / Local</option>
                                <option value="Sophomore">Regional</option>
                                <option value="Junior">National</option>
                                <option value="Senior">Elite / International</option>
                            </select>
                        </Field>

                        <Field
                            label="Annual cost of competing"
                            helper="Add it all up — equipment, competition travel, coaching, and entry fees for the season."
                        >
                            <PrefixedInput value={coa} onChange={setCoa} placeholder="45000" />
                        </Field>

                        <Field
                            label="Sponsorships + grants per year"
                            helper="Club support, sporting grants, sponsor contributions — anything you don't repay."
                        >
                            <PrefixedInput value={grants} onChange={setGrants} placeholder="8000" />
                        </Field>

                        <Field
                            label="Family contribution per year"
                            helper="What your family can pay out of pocket — savings, income. Estimate if unsure."
                        >
                            <PrefixedInput value={family} onChange={setFamily} placeholder="5000" />
                        </Field>

                        {error && (
                            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
                                {error}
                            </p>
                        )}

                        <Button type="submit" size="lg" className="w-full text-base py-6 rounded-full font-semibold">
                            Show me my gap
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </form>

                    {/* Results */}
                    {submitted && result && verdict && (
                        <div
                            ref={resultsRef}
                            className="mt-8 bg-white rounded-2xl p-6 md:p-8 border-2 border-primary shadow-md"
                            aria-live="polite"
                        >
                            <p className="text-sm text-muted-foreground mb-1">Your annual season funding gap:</p>
                            <p className="text-5xl md:text-6xl font-bold text-primary leading-none mb-3">
                                {dollars(result.annualGap)}
                            </p>
                            <p className="text-muted-foreground mb-6">
                                Roughly <strong className="text-foreground">{dollars(result.monthlyGap)}/month</strong> in sport costs
                                you'd need to cover from somewhere new for the 2026 season.
                            </p>

                            <div className="bg-secondary/10 rounded-lg p-4 mb-6 text-sm space-y-1.5">
                                <BreakdownRow label="Cost of competing" value={dollars(result.coa)} />
                                <BreakdownRow label="− Sponsorships + grants" value={'-' + dollars(result.grants)} />
                                <BreakdownRow label="− Existing financial support" value={'-' + dollars(result.federalMax)} />
                                <BreakdownRow label="− Family contribution" value={'-' + dollars(result.family)} />
                                <BreakdownRow label="− Other funding (capped)" value={'-' + dollars(result.newPlusCoverage)} />
                                <div className="border-t border-border mt-2 pt-2">
                                    <BreakdownRow label="Your gap" value={dollars(result.annualGap)} bold />
                                </div>
                            </div>

                            <div className={`rounded-lg p-4 mb-6 text-sm leading-relaxed border ${verdict.classes}`}>
                                {verdict.message}
                            </div>

                            {result.annualGap > 0 ? (
                                <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-5 md:p-6 border border-primary/20">
                                    <div className="flex items-start gap-3 mb-4">
                                        <Users className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-base md:text-lg font-semibold text-foreground mb-1">
                                                {result.backersNeeded} backers at $50/month closes this gap.
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                That's the math. Set up your free Fund Page on Sport Fund and start
                                                inviting your club, teammates, family friends, and local community. Small
                                                recurring amounts add up — and renew every month so you don't burn out
                                                your network with one-shot asks.
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        size="lg"
                                        className="w-full text-base py-6 rounded-full font-semibold group"
                                        onClick={goToSignup}
                                    >
                                        Set up my free Fund Page
                                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                    <p className="text-xs text-muted-foreground text-center mt-3">
                                        Free to set up · 3 minutes · No card required
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-5 md:p-6 border border-primary/20">
                                    <div className="flex items-start gap-3 mb-4">
                                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-base md:text-lg font-semibold text-foreground mb-1">
                                                You don't have a gap this year — but build the safety net anyway.
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Sport costs can spike without warning — an injury, a new bike, or
                                                qualifying for a far-away competition. A small backer community of
                                                5–10 people on Sport Fund is your insurance policy if costs jump mid-season.
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        size="lg"
                                        className="w-full text-base py-6 rounded-full font-semibold group"
                                        onClick={goToSignup}
                                    >
                                        Build my backup community
                                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* How Sport Fund closes the gap */}
            <section
                ref={gtmRef}
                className={`py-20 px-6 transition-all duration-1000 ${gtmVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            How Sport Fund closes the gap
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Not another GoFundMe. A Fund Page built for recurring monthly support from the people who
                            already believe in you.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <HowCard
                            icon={Users}
                            title="Your community, not strangers"
                            body="Club mates, mentors, family friends, local supporters. People who already know your name — and want to invest $50/month in your sport."
                        />
                        <HowCard
                            icon={TrendingUp}
                            title="Recurring beats one-time"
                            body="A $50/month backer gives $600/year. Ten of them gives $6,000/year — every season you compete. One GoFundMe push can't do that."
                        />
                        <HowCard
                            icon={CheckCircle}
                            title="Set up in 3 minutes, free"
                            body="No credit card to start. Stripe-powered payouts go straight to your bank. We take 5% to keep the lights on — that's it."
                        />
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section
                ref={ctaRef}
                className={`py-20 bg-primary transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                        Don't wait for the season to start.
                    </h2>
                    <p className="text-xl text-white/90 mb-10 leading-relaxed">
                        Training and travel decisions happen now. Run the numbers, set up your page, and start the chain
                        before the season kicks off.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="#calc">
                            <Button
                                size="lg"
                                className="bg-white hover:bg-white/90 text-primary text-lg px-8 py-6 rounded-full font-semibold"
                            >
                                Calculate my gap
                            </Button>
                        </a>
                        <Button
                            size="lg"
                            variant="outline"
                            className="bg-transparent text-white border-white hover:bg-white/10 hover:text-white text-lg px-8 py-6 rounded-full font-semibold"
                            onClick={() => navigate('/signup-creator?source=parent-plus-calculator')}
                        >
                            Set up my Fund Page
                        </Button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function StatCard({ num, label }: { num: string; label: string }) {
    return (
        <div className="bg-secondary/10 border border-border rounded-2xl p-6 text-center">
            <p className="text-3xl md:text-4xl font-bold text-primary tracking-tight mb-2">{num}</p>
            <p className="text-sm text-muted-foreground leading-snug">{label}</p>
        </div>
    );
}

function Field({
    label,
    helper,
    children,
}: {
    label: string;
    helper?: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <label className="block text-sm font-semibold text-foreground mb-1">{label}</label>
            {helper && <p className="text-xs text-muted-foreground mb-2">{helper}</p>}
            {children}
        </div>
    );
}

function PrefixedInput({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
}) {
    return (
        <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">$</span>
            <input
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full pl-8 pr-4 py-3 text-base border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
        </div>
    );
}

function BreakdownRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
    return (
        <div className={`flex justify-between ${bold ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
}

function HowCard({
    icon: Icon,
    title,
    body,
}: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    body: string;
}) {
    return (
        <div className="bg-white rounded-2xl p-6 border border-border shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
        </div>
    );
}

function getVerdict(gap: number, backersNeeded: number): { message: React.ReactNode; classes: string } {
    if (gap === 0) {
        return {
            classes: 'bg-green-50 border-green-200 text-green-900',
            message: (
                <>
                    <strong>Your sponsorships + family contribution + other funding covers your cost.</strong> You
                    don't have a season funding gap this year. The bigger risk now: <strong>sport costs can spike
                    without warning</strong>, so building a backup community is still worth it.
                </>
            ),
        };
    }
    if (gap < 5000) {
        return {
            classes: 'bg-green-50 border-green-200 text-green-900',
            message: (
                <>
                    <strong>Very closeable.</strong> A focused recurring-support push can cover this. At this level
                    most families fill the gap in 4–6 weeks with organized outreach to {backersNeeded}–{backersNeeded * 2} backers.
                </>
            ),
        };
    }
    if (gap < 15000) {
        return {
            classes: 'bg-amber-50 border-amber-200 text-amber-900',
            message: (
                <>
                    <strong>Doable, but it needs the right audience.</strong> At this level you're looking for
                    sustained monthly support, not a one-time GoFundMe push. Recurring backers spread across your club,
                    mentors, family friends, and local community — small amounts that add up.
                </>
            ),
        };
    }
    return {
        classes: 'bg-red-50 border-red-200 text-red-900',
        message: (
            <>
                <strong>This is the strongest case for community funding.</strong> A gap this large needs both a
                recurring backer community AND a push for additional sponsorship or club support. Don't soften this
                number — it's real, and so are your options.
            </>
        ),
    };
}
